<template>
  <div>
    <div :class="$vuetify.breakpoint.mdAndUp ? 'mb-10' : 'mb-8'">
      <BaseButton
        :text="`Back to 'Your details'`"
        flat
        ripple
        class="font-weight-bold d-flex ml-n5"
        icon="mdi-chevron-left"
        color="#106175"
        small
        @click="backToYourDetails"
      />
      <div
        :class="$vuetify.breakpoint.mdAndUp ? 'mt-10' : 'mt-6'"
        class="d-flex justify-space-between"
      >
        <h2 class="font-24 font-lg-28 font-weight-bold primary--text">
          Cover details
        </h2>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3 mt-1"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="saveAndExit"
        />
      </div>
    </div>
    <ValidationObserver
      ref="observerFormDetails"
      v-slot="{ validate, invalid }"
    >
      <form @submit.prevent="validate().then(() => onSubmit(invalid))">
        <BaseLabelDialog
          text="What type of cover do you need?"
          class="mb-2 label-text"
          dialog-component-name="DialogContentMPPICoverType"
          @showModal="showModal"
        />

        <BaseSelect
          v-model="questionCoverType.Value"
          vid="CoverType"
          dataUiid="CoverType"
          :hideDetails="false"
          :items="coverTypeOptions"
          :rules="coverTypeRules"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        />

        <BaseAlert
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          color="var(--blue-100)"
          class="px-4 py-3 ma-0 border-sm border-solid border-info"
        >
          <div class="d-flex">
            <div class="mr-3">
              <v-icon color="var(--blue-500)">mdi-information</v-icon>
            </div>
            <div class="primary--text">
              If your customer is completing a new mortgage or remortgage, or if
              this policy is being purchased to replace existing cover, then
              there is no initial exclusion period. If your customer is an
              existing borrower, then any unemployment in the first 90 days
              following the start date will not be covered.
            </div>
          </div>
        </BaseAlert>

        <template v-if="questionCoverType.Value">
          <h2 class="mt-8 mb-5 font-20 font-weight-bold primary--text">
            Accident and sickness options
          </h2>
          <BaseLabelDialog
            text="Accident and sickness type"
            class="mt-3 mb-2 label-text"
            dialog-component-name="DialogContentMPPIQualTypeDisability"
            @showModal="showModal"
          />
          <BaseSelect
            v-model="questionQualTypeDisability.Value"
            vid="QualTypeDisability"
            dataUiid="QualTypeDisability"
            :hideDetails="false"
            :items="qualTypeDisabilityOptions"
            :rules="qualTypeDisabilityRules"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            @change="
              resetQuestionValue('questionQualPeriodDisability', 'ThirtyDays')
            "
          />

          <BaseLabelDialog
            text="Accident and sickness qualification period"
            class="mt-3 mb-2 label-text"
            dialog-component-name="DialogContentMPPIQualPeriodDisability"
            @showModal="showModal"
          />
          <BaseRadio
            pslStyle="button"
            v-model="questionQualPeriodDisability.Value"
            vid="QualPeriodDisability"
            dataUiid="QualPeriodDisability"
            :hideDetails="false"
            :items="qualPeriodDisabilityOptions"
            :rules="qualPeriodDisabilityRules"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :width="$vuetify.breakpoint.smAndDown ? 'calc(50% - 6px)' : '132'"
          />
        </template>

        <template
          v-if="questionCoverType.Value === 'UnemploymentAndDisability'"
        >
          <h2 class="mt-3 mb-5 font-20 font-weight-bold primary--text">
            Unemployment options
          </h2>

          <BaseLabelDialog
            text="Unemployment cover type"
            class="mt-3 mb-2 label-text"
            dialog-component-name="DialogContentMPPIQualTypeUnemployment"
            @showModal="showModal"
          />
          <BaseSelect
            v-model="questionQualTypeUnemployment.Value"
            vid="QualTypeUnemployment"
            dataUiid="QualTypeUnemployment"
            :hideDetails="false"
            :items="qualTypeUnemploymentOptions"
            :rules="qualTypeUnemploymentRules"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            @change="
              resetQuestionValue('questionQualPeriodUnemployment', 'ThirtyDays')
            "
          />

          <BaseLabelDialog
            text="Unemployment qualification period"
            class="mt-3 mb-2 label-text"
            dialog-component-name="DialogContentMPPIQualPeriodUnemployment"
            @showModal="showModal"
          />
          <BaseRadio
            pslStyle="button"
            v-model="questionQualPeriodUnemployment.Value"
            vid="QualPeriodUnemployment"
            dataUiid="QualPeriodUnemployment"
            :hideDetails="false"
            :items="qualPeriodUnemploymentOptions"
            :rules="qualPeriodUnemploymentRules"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :width="$vuetify.breakpoint.smAndDown ? 'calc(50% - 6px)' : '132'"
          />
        </template>

        <BaseLabel
          text="Benefit period"
          class="mt-4 mb-2 label-text"
          helpText="The benefit period is the maximum number of monthly benefit payments the customer would receive for a single claim event. <br /> Customers are able to make further claims subject to the terms and conditions of the policy."
        />
        <BaseRadio
          pslStyle="button"
          v-model="questionPolicyBenefitPeriod.Value"
          vid="PolicyBenefitPeriod"
          dataUiid="PolicyBenefitPeriod"
          :hideDetails="false"
          :items="policyBenefitPeriodOptions"
          :rules="policyBenefitPeriodRules"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :width="$vuetify.breakpoint.smAndDown ? 'calc(50% - 6px)' : '162'"
        />

        <div class="mt-4 mb-2">
          <h2 class="mb-4 font-20 font-weight-bold primary--text">
            Benefit amount
          </h2>
          <div class="d-flex">
            <div v-if="$vuetify.breakpoint.mdAndUp" class="border-line"></div>
            <div :style="{ width: '100%' }">
              <BaseLabel
                text="Monthly mortgage payment"
                class="mb-2 label-text"
                :helpText="helpTextData.MonthlyMortgagePayment"
              />
              <BaseInput
                prefix="£"
                moneyFormat
                vid="MonthlyMortgagePayment"
                dataUiid="MonthlyMortgagePayment"
                v-model="questionMonthlyMortgagePayment.Value"
                class="larger-error-message"
                className="mb-3"
                :validationOnMount="false"
                :hideDetails="false"
                :rules="monthlyMortgagePaymentRules"
                :style="{
                  'max-width': $vuetify.breakpoint.lgAndUp ? '160px' : '100%',
                }"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              />

              <BaseLabel
                text="Associated premiums"
                class="my-2 label-text"
                :helpText="helpTextData.AssociatedPremiums"
              />
              <BaseInput
                prefix="£"
                moneyFormat
                vid="OtherCover"
                dataUiid="OtherCover"
                v-model="questionOtherCover.Value"
                class="larger-error-message"
                className="mb-4"
                :validationOnMount="false"
                :hideDetails="false"
                :rules="otherCoverRules"
                :style="{
                  'max-width': $vuetify.breakpoint.lgAndUp ? '160px' : '100%',
                }"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              />

              <BaseLabel
                text="Extra cover"
                class="my-2 label-text"
                :helpText="helpTextData.BenefitUpliftAmount"
              />
              <BaseInput
                prefix="£"
                moneyFormat
                vid="BenefitUpliftAmount"
                dataUiid="BenefitUpliftAmount"
                v-model="questionBenefitUpliftAmount.Value"
                class="larger-error-message"
                :validationOnMount="false"
                :hideDetails="false"
                :rules="benefitUpliftAmountRules"
                :style="{
                  'max-width': $vuetify.breakpoint.lgAndUp ? '160px' : '100%',
                }"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              />
            </div>
          </div>
        </div>

        <h2>
          Total monthly benefit: {{ totalMonthlyBenefit | formatCurrency }}
          <v-tooltip top color="primary" content-class="tooltipTop primary">
            <template v-slot:activator="{ on, attrs }"
              ><v-icon
                color="accent"
                class="tooltip text-decoration-none"
                v-on="on"
                v-bind="attrs"
                >mdi-help-circle</v-icon
              ></template
            ><span v-html="helpTextData.TotalMonthlyBenefit"></span
          ></v-tooltip>
        </h2>
        <div
          class="invisible-input"
          :class="{
            'mb-11': questionSecondApplicantIncluded.Value === true,
          }"
        >
          <BaseInput
            ref="totalMonthly"
            hideDetails="auto"
            :value="totalMonthlyBenefit"
            :rules="totalMonthlyBenefitRules"
          />
        </div>

        <template v-if="questionSecondApplicantIncluded.Value === true">
          <BaseLabel
            text="How much of the total monthly benefit should <b>Applicant 1</b> receive in the event they make a claim?"
            class="mb-2 label-text"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '515px' : '100%',
            }"
            helpText="This is the total amount of monthly benefit applicant 1 would receive in the event of a claim. The remaining monthly benefit amount would be received by applicant 2 in the event they need to make a claim. <br /> If the applicants wish to insure 100% of the monthly benefit each, then they must each take out a separate policy for the full monthly benefit amount."
          />
          <BaseInput
            prefix="£"
            moneyFormat
            v-model="questionApplicant1Benefit.Value"
            vid="Applicant1Benefit"
            dataUiid="Applicant1Benefit"
            class="larger-error-message"
            :validationOnMount="false"
            :hideDetails="false"
            :rules="applicant1BenefitRules"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '160px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />
          <div
            v-if="applicant2Benefit >= 0"
            class="mt-3 mb-4"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
          >
            <span
              class="label-text black2-color font-18 letter-spacing-4 font-weight-medium"
            >
              <b>Applicant 2</b> will receive
              <b>{{ applicant2Benefit | formatCurrency }}</b> of the total
              monthly benefit in the event they make a claim.
            </span>

            <div class="invisible-input">
              <BaseInput
                ref="applicant2Benefit"
                hideDetails="auto"
                :value="applicant2Benefit"
                :rules="applicant2BenefitRules"
              />
            </div>
          </div>
        </template>

        <BaseButton
          type="submit"
          depressed
          name="add"
          rounded
          dense
          class="font-weight-bold font-20 pa-7 px-13 primary--text"
          text="GET QUOTES"
          :block="$vuetify.breakpoint.lgAndUp ? false : true"
          :color="'psorange'"
          :class="{
            'mt-10': questionSecondApplicantIncluded.Value !== true,
            'mt-6': questionSecondApplicantIncluded.Value === true,
          }"
        />
      </form>
    </ValidationObserver>

    <EligibilityBaseDialog ref="dialog" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <Eligibility
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogLoading="$refs.dialogLoading"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :listEligibility="MPPI_ELIGIBILITY"
          :inforMsg="MPPI_ELIGIBILITY_INFO_MSG"
          :title="title"
          :customHandleConfirmEligibility="true"
          @confirmEligibility="createQuote()"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogLoading" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ title }">
        <EligibilityLoading
          image-source=""
          backgroundMessageColor="var(--blue-100)"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="var(--blue-500)"
          borderRadius="4px"
          :title="title"
          :gettingQuoteMessage="GETTING_QUOTE_MPPI_MESSAGE"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogNotMet" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <EligibilityNotMet
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :title="title"
        />
      </template>
    </EligibilityBaseDialog>
  </div>
</template>
<script>
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseRadio from "@/components/BaseComponents/BaseRadio.vue";
import BaseSelect from "@/components/BaseComponents/BaseSelect.vue";
import BaseLabelDialog from "@/components/BaseComponents/BaseLabelDialog.vue";
import BaseAlert from "@/components/BaseComponents/BaseAlert.vue";

import EligibilityBaseDialog from "@/components/EligibilityBaseDialog.vue";
import Eligibility from "@/views/Dialog/Eligibility.vue";
import EligibilityLoading from "@/views/Dialog/EligibilityLoading.vue";
import EligibilityNotMet from "@/views/Dialog/EligibilityNotMet.vue";

import { DIALOG_MODULE, QUESTIONS_MODULE } from "@/store/types/module-types";
import {
  MPPI_ELIGIBILITY,
  MPPI_ELIGIBILITY_INFO_MSG,
} from "@/constants/eligibility.constants";
import { GETTING_QUOTE_MPPI_MESSAGE } from "@/constants/message.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import { TooltipHelpText } from "@/constants/helptext.constants";

import { EventBus } from "@/common/eventbus";
import { addFieldLabel, convertToBool, scrollTop } from "@/common/utils";
import { setItemDataUiids } from "@/constants/component.constants";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapMutations, mapState } from "vuex";
import numeral from "numeral";
import {
  MPPI_COVER_TYPE_OPTIONS,
  MPPI_POLICY_BENEFIT_PERIOD_OPTIONS,
  MPPI_QUAL_PERIOD_OPTIONS,
  MPPI_QUAL_TYPE_OPTIONS,
} from "@/constants/answer.constants";

import { extend } from "vee-validate";
import { AMOUNT_DEFAULT_FORMAT } from "@/constants/currency-format.constants";

extend("maximumBenefitUpliftAmount", {
  params: [
    "applicant1MonthlyIncome",
    "applicant2MonthlyIncome",
    "monthlyMortgage",
    "otherCover",
  ],
  validate: (
    value,
    {
      applicant1MonthlyIncome,
      applicant2MonthlyIncome,
      monthlyMortgage,
      otherCover,
    }
  ) => {
    const numberValue = numeral(value).value();

    const monthlyMortgageAndOtherCover = (
      0.33 *
      (monthlyMortgage + otherCover)
    ).toFixed(2);

    const grossIncome = (
      0.75 *
      (applicant1MonthlyIncome + applicant2MonthlyIncome)
    ).toFixed(2);

    // check value with monthly mortgage first then with gross income
    const maximum =
      +monthlyMortgageAndOtherCover < +grossIncome
        ? monthlyMortgageAndOtherCover
        : grossIncome;

    return (
      +maximum >= numberValue ||
      `Extra cover must not exceed £${numeral(maximum).format(
        AMOUNT_DEFAULT_FORMAT
      )}.`
    );
  },
});

extend("maxiumTotalMonthly", {
  params: ["applicant1MonthlyIncome", "applicant2MonthlyIncome"],
  validate: (value, { applicant1MonthlyIncome, applicant2MonthlyIncome }) => {
    const numberValue = numeral(value).value();
    if (value > 3000.0) {
      return "Maximum benefit cannot exceed £3,000.";
    }
    const maximumBenefit = (
      0.75 *
      (applicant1MonthlyIncome + applicant2MonthlyIncome)
    ).toFixed(2);
    return (
      numberValue <= +maximumBenefit ||
      `Maximum benefit cannot exceed £${numeral(maximumBenefit).format(
        AMOUNT_DEFAULT_FORMAT
      )}.`
    );
  },
});

extend("maximumApplicant1Benefit", {
  params: ["totalMonthlyBenefit", "applicant1MonthlyIncome"],
  validate: (value, { totalMonthlyBenefit, applicant1MonthlyIncome }) => {
    const parsedValue = numeral(value).value();
    const maxValueByMonthly = totalMonthlyBenefit - 1;
    const maxValueByIncome = (0.75 * applicant1MonthlyIncome).toFixed(2);

    if (
      maxValueByMonthly > 0 &&
      maxValueByMonthly < +maxValueByIncome &&
      parsedValue > maxValueByMonthly
    ) {
      return `Please enter an amount less than or equal to £${numeral(
        maxValueByMonthly
      ).format(AMOUNT_DEFAULT_FORMAT)}`;
    }

    if (+maxValueByIncome < parsedValue) {
      return `Maximum benefit for applicant 1 cannot exceed £${numeral(
        maxValueByIncome
      ).format(AMOUNT_DEFAULT_FORMAT)}`;
    }

    return true;
  },
});

export default {
  name: "MPPICoverDetailsPage",
  components: {
    BaseSelect,
    BaseLabel,
    BaseButton,
    BaseRadio,
    BaseInput,
    BaseLabelDialog,
    BaseAlert,

    Eligibility,
    EligibilityLoading,
    EligibilityBaseDialog,
    EligibilityNotMet,

    ValidationObserver,
  },
  data() {
    return {
      MPPI_ELIGIBILITY,
      MPPI_ELIGIBILITY_INFO_MSG,
      GETTING_QUOTE_MPPI_MESSAGE,
      helpTextData: TooltipHelpText.MPPI,

      questionCoverType: {},
      questionQualTypeDisability: {},
      questionQualPeriodDisability: {},
      questionQualTypeUnemployment: {},
      questionQualPeriodUnemployment: {},
      questionPolicyBenefitPeriod: {},
      questionMonthlyMortgagePayment: {},
      questionOtherCover: {},
      questionBenefitUpliftAmount: {},
      questionTotalMonthlyBenefit: {},
      questionSecondApplicantIncluded: {},
      questionApplicant1Benefit: {},
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, ["questionMap"]),
    coverTypeRules() {
      return addFieldLabel(
        {
          required: true,
        },
        "Type of cover"
      );
    },
    coverTypeOptions() {
      return setItemDataUiids("CoverType", MPPI_COVER_TYPE_OPTIONS);
    },

    qualTypeDisabilityRules() {
      return addFieldLabel(
        {
          required: true,
        },
        "Accident & sickness qualification type"
      );
    },
    qualTypeDisabilityOptions() {
      return setItemDataUiids("QualTypeDisability", MPPI_QUAL_TYPE_OPTIONS);
    },

    qualTypeUnemploymentRules() {
      return addFieldLabel(
        {
          required: true,
        },
        "Unemployment cover type"
      );
    },
    qualTypeUnemploymentOptions() {
      return setItemDataUiids("QualTypeUnemployment", MPPI_QUAL_TYPE_OPTIONS);
    },

    qualPeriodUnemploymentRules() {
      return addFieldLabel(
        {
          required: true,
        },
        "Unemployment qualification period"
      );
    },
    qualPeriodUnemploymentOptions() {
      const options =
        this.questionQualTypeUnemployment.Value === "DayOne"
          ? MPPI_QUAL_PERIOD_OPTIONS.filter(
              (i) => i.value !== "OneHundredEightyDays"
            )
          : MPPI_QUAL_PERIOD_OPTIONS;

      return setItemDataUiids("QualPeriodUnemployment", options);
    },

    policyBenefitPeriodRules() {
      return addFieldLabel(
        {
          required: {},
        },
        "Benefit period"
      );
    },
    policyBenefitPeriodOptions() {
      return setItemDataUiids(
        "PolicyBenefitPeriod",
        MPPI_POLICY_BENEFIT_PERIOD_OPTIONS
      );
    },

    qualPeriodDisabilityRules() {
      return addFieldLabel(
        {
          required: {},
        },
        "Accident and sickness qualification period"
      );
    },
    qualPeriodDisabilityOptions() {
      const options =
        this.questionQualTypeDisability.Value === "DayOne"
          ? MPPI_QUAL_PERIOD_OPTIONS.filter(
              (i) => i.value !== "OneHundredEightyDays"
            )
          : MPPI_QUAL_PERIOD_OPTIONS;

      return setItemDataUiids("QualPeriodDisability", options);
    },

    monthlyMortgagePaymentRules() {
      return addFieldLabel(
        {
          required: {},
          min_currency_value: {
            minValue: 100.0,
            inclusive: true,
            customMessage:
              "We can only cover a monthly mortgage payment between £100 and £3,000.",
          },
          max_value: {
            max: 3000.0,
            customMessage:
              "We can only cover a monthly mortgage payment between £100 and £3,000.",
          },
        },
        "Monthly mortgage payment"
      );
    },

    otherCoverRules() {
      return addFieldLabel(
        {
          required: {},
          max_value: {
            max: 3000.0,
            customMessage: "Associated premiums must not exceed £3,000.",
          },
        },
        "Associated premiums"
      );
    },

    benefitUpliftAmountRules() {
      return addFieldLabel(
        {
          required: {},
          maximumBenefitUpliftAmount: {
            applicant1MonthlyIncome: this.applicant1MonthlyIncome,
            applicant2MonthlyIncome: this.applicant2MonthlyIncome,
            monthlyMortgage: this.monthlyMortgagePayment,
            otherCover: this.otherCover,
          },
        },
        "Extra cover"
      );
    },

    applicant1BenefitRules() {
      return addFieldLabel(
        {
          required: {},
          max: {
            length: 8,
            errorMessageTemplate: "{0} may not be greater than 8 characters.",
          },
          min_currency_value: {
            minValue: 0,
            customMessage: "You must supply a benefit amount for customer 1.",
          },
          maximumApplicant1Benefit: {
            applicant1MonthlyIncome: this.applicant1MonthlyIncome,
            totalMonthlyBenefit: this.totalMonthlyBenefit,
          },
        },
        "Applicant 1 benefit"
      );
    },

    totalMonthlyBenefitRules() {
      return addFieldLabel(
        {
          maxiumTotalMonthly: {
            applicant1MonthlyIncome: this.applicant1MonthlyIncome,
            applicant2MonthlyIncome: this.applicant2MonthlyIncome,
          },
        },
        "Total monthly benefit"
      );
    },

    totalMonthlyBenefit() {
      const total =
        (Math.round(this.monthlyMortgagePayment * 100) +
          Math.round(this.otherCover * 100) +
          Math.round(this.benefitUpliftAmount * 100)) /
        100;
      return total;
    },

    monthlyMortgagePayment() {
      return numeral(this.questionMonthlyMortgagePayment.Value).value() || 0.0;
    },

    otherCover() {
      return numeral(this.questionOtherCover.Value).value() || 0.0;
    },

    benefitUpliftAmount() {
      return numeral(this.questionBenefitUpliftAmount.Value).value() || 0.0;
    },

    applicant1MonthlyIncome() {
      return (
        numeral(this.questionMap.Applicant1GrossMonthlyIncome.Value).value() ||
        0.0
      );
    },

    applicant2MonthlyIncome() {
      return (
        numeral(this.questionMap.Applicant2GrossMonthlyIncome.Value).value() ||
        0.0
      );
    },

    applicant1Benfit() {
      return numeral(this.questionApplicant1Benefit.Value).value() || 0.0;
    },

    applicant2Benefit() {
      return this.totalMonthlyBenefit - this.applicant1Benfit;
    },
    maxmiumApplicant2Benefit() {
      return +(0.75 * this.applicant2MonthlyIncome).toFixed(2);
    },
    applicant2BenefitRules() {
      return addFieldLabel(
        {
          max_value: {
            max: this.maxmiumApplicant2Benefit,
            customMessage: `Maximum benefit for applicant 2 cannot exceed £${numeral(
              this.maxmiumApplicant2Benefit.toFixed(2)
            ).format(AMOUNT_DEFAULT_FORMAT)}.`,
          },
        },
        "Applicant 1 benefit"
      );
    },
  },
  watch: {
    totalMonthlyBenefit(value) {
      this.questionTotalMonthlyBenefit.Value = value;
      this.$refs.totalMonthly?.$refs.provider?.validate();
    },
    applicant2Benefit() {
      if (this.questionSecondApplicantIncluded.Value === true) {
        this.$refs.applicant2Benefit?.$refs.provider?.validate();
      }
    },
    "questionSecondApplicantIncluded.Value"(value) {
      if (!value) {
        this.questionApplicant1Benefit.Value = null;
      }
    },
  },
  methods: {
    ...mapMutations(DIALOG_MODULE, ["showModal"]),
    ...mapActions(QUESTIONS_MODULE, [
      "toggleGlobalSaveAndExitDialog",
      "setRestoringLastStep",
      "setCurrentRefObserverForm",
      "setCoverAnswers",
      "getQuotes",
      "setIsExpiredFlag",
      "refreshSessionAndNextStep",
    ]),
    saveAndExit() {
      this.toggleGlobalSaveAndExitDialog();
    },
    onSubmit(invalid) {
      if (invalid) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }
      this.$refs.dialog.open("Eligibility check");
    },
    createQuote() {
      this.$refs.dialog.cancel();
      this.$refs.dialogLoading.open("Hang tight, getting quotes...");

      this.setApplicant1Eligibility();

      this.setApplicant2Eligibility();

      const quoteRequestFields = {
        IsPartialQuote: false,
      };

      this.getQuotes(quoteRequestFields)
        .then(() => {
          this.setIsExpiredFlag(false);
          this.$refs.dialog.cancel();
          this.refreshSessionAndNextStep(ROUTE_NAME.MPPI.B2B.QUOTATION_RESULT);
        })
        .catch(() => {
          this.$refs.dialogLoading.cancel();
        });
    },
    setApplicant1Eligibility() {
      this.questionMap.Applicant1Aged18To64.Value = true;
      this.questionMap.Applicant1LivingUK.Value = true;
      this.questionMap.Applicant1SixteenHours.Value = true;
      this.questionMap.Applicant1NamedOnMortgage.Value = true;
      this.questionMap.Applicant1ResidentialMortgage.Value = true;
      this.questionMap.Applicant1RepaymentsUpToDate.Value = true;
      this.questionMap.Applicant1MedicalCondition.Value = true;

      this.questionMap.Applicant1TemporaryWork.Value = false;
    },
    setApplicant2Eligibility() {
      if (this.questionSecondApplicantIncluded.Value === true) {
        this.questionMap.Applicant2Aged18To64.Value = true;
        this.questionMap.Applicant2LivingUK.Value = true;
        this.questionMap.Applicant2SixteenHours.Value = true;
        this.questionMap.Applicant2NamedOnMortgage.Value = true;
        this.questionMap.Applicant2ResidentialMortgage.Value = true;
        this.questionMap.Applicant2RepaymentsUpToDate.Value = true;
        this.questionMap.Applicant2MedicalCondition.Value = true;

        this.questionMap.Applicant2TemporaryWork.Value = false;
      } else {
        this.questionMap.Applicant2Aged18To64.Value = null;
        this.questionMap.Applicant2LivingUK.Value = null;
        this.questionMap.Applicant2SixteenHours.Value = null;
        this.questionMap.Applicant2NamedOnMortgage.Value = null;
        this.questionMap.Applicant2ResidentialMortgage.Value = null;
        this.questionMap.Applicant2RepaymentsUpToDate.Value = null;
        this.questionMap.Applicant2MedicalCondition.Value = null;

        this.questionMap.Applicant2TemporaryWork.Value = null;
      }
    },
    backToYourDetails() {
      this.$router.push({ name: ROUTE_NAME.MPPI.B2B.YOUR_DETAILS });
    },
    resetQuestionValue(key, value = "") {
      if (!this[key]) return;
      this[key].Value = value;
    },
  },
  created() {
    // setup data from quote retrieval
    this.questionCoverType = this.questionMap.CoverType;
    this.questionQualTypeDisability = this.questionMap.QualTypeDisability;
    this.questionQualPeriodDisability = this.questionMap.QualPeriodDisability;
    this.questionQualTypeUnemployment = this.questionMap.QualTypeUnemployment;
    this.questionQualPeriodUnemployment =
      this.questionMap.QualPeriodUnemployment;
    this.questionPolicyBenefitPeriod = this.questionMap.PolicyBenefitPeriod;
    this.questionMonthlyMortgagePayment =
      this.questionMap.MonthlyMortgagePayment;
    this.questionOtherCover = this.questionMap.OtherCover;
    this.questionBenefitUpliftAmount = this.questionMap.BenefitUpliftAmount;
    this.questionTotalMonthlyBenefit = this.questionMap.TotalMonthlyBenefit;

    this.questionSecondApplicantIncluded =
      this.questionMap.SecondApplicantIncluded;
    if (this.questionSecondApplicantIncluded) {
      this.questionSecondApplicantIncluded.Value = convertToBool(
        this.questionSecondApplicantIncluded.Value
      );
    }

    this.questionApplicant1Benefit = this.questionMap.Applicant1Benefit;
  },
  mounted() {
    scrollTop();
    this.setRestoringLastStep(false);
    this.setCurrentRefObserverForm(this.$refs.observerFormDetails);
  },
};
</script>
<style lang="scss" scoped>
.border-line {
  border-left: 2px solid #007185;
  width: 24px;
  height: 327px;
}

.label-text {
  line-height: 21.94px;
}

.larger-error-message {
  :deep(.v-text-field__details .v-messages__message) {
    min-width: 440px;
  }
}

@media screen and (max-width: $grid-breakpoints-md) {
  .larger-error-message {
    :deep(.v-text-field__details .v-messages__message) {
      min-width: unset;
    }
  }
}

.invisible-input {
  padding-top: 0;
  padding-bottom: 0;
  :deep(.v-input) {
    padding-top: 0;
    margin-top: 0;
    .v-input__control {
      .v-input__slot {
        display: none;
      }
    }
    .v-text-field__details {
      margin-top: 4px;
      margin-bottom: 0;
    }
  }
}

i.tooltip.v-icon.v-icon {
  font-size: 18px !important;
  top: -2px !important;
  position: relative !important;
}
</style>
